/**
 * Format a number as British Pounds currency
 * @param {number} amount - The amount to format
 * @param {boolean} [showSymbol=true] - Whether to show the £ symbol
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, showSymbol = true) => {
  if (amount === undefined || amount === null) return 'N/A';
  
  const formattedAmount = Number(amount).toFixed(2);
  return showSymbol ? `£${formattedAmount}` : formattedAmount;
};

/**
 * Format a number as a percentage
 * @param {number} value - The value to format
 * @param {number} total - The total value
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, total) => {
  if (!total) return '0%';
  return `${Math.round((value / total) * 100)}%`;
};

/**
 * Format a date string
 * @param {string|Date} date - The date to format
 * @param {string} locale - The locale to use (default: 'en-GB')
 * @returns {string} The formatted date string
 */
export const formatDate = (date, locale = 'en-GB') => {
  if (!date) return '';
  
  const options = { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric' 
  };

  return new Date(date).toLocaleDateString(locale, options);
};

/**
 * Format a datetime string
 * @param {string|Date} datetime - The datetime to format
 * @param {string} locale - The locale to use (default: 'en-GB')
 * @returns {string} The formatted datetime string
 */
export const formatDateTime = (datetime, locale = 'en-GB') => {
  if (!datetime) return '';
  
  const options = { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };

  return new Date(datetime).toLocaleString(locale, options);
};

/**
 * Format a phone number
 * @param {string} phone - The phone number to format
 * @returns {string} The formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Check if it's a UK number format (starts with 0)
  if (cleaned.startsWith('0') && cleaned.length === 11) {
    // Format as: 01234 567890
    return cleaned.replace(/(\d{5})(\d{6})/, '$1 $2');
  }
  
  // Fallback for other formats
  return phone;
};

/**
 * Capitalize the first letter of each word in a string
 * @param {string} str - The string to capitalize
 * @returns {string} The capitalized string
 */
export const capitalize = (str) => {
  if (!str) return '';
  
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Format a file size in bytes to a human-readable format
 * @param {number} bytes - The file size in bytes
 * @param {number} decimals - The number of decimal places (default: 2)
 * @returns {string} The formatted file size
 */
export const formatFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}; 